import React, {useState, useEffect, useMemo, useRef} from 'react';
import {createPortal} from 'react-dom';
import {Car, RelatedCar, ContextMenuGroup, ContextActionKey, ContextMenuState} from '../../types';
import {useTableSearch} from '../../hooks/useTableSearch';
import {useDragSelect} from '../../hooks/useDragSelect';
import {useTablePreferences} from '../../hooks/useTablePreferences';
import {TableColumn} from '../../types/table';
import {filterRowsByColumn} from '../../search/filterByColumn';
import {getCellValue} from '../../utils/tableUtils';
import {getCarsColumns, MONOSPACE_COLUMNS} from './carsColumns';
import {ContextMenu, PrintBLOptions, PrintBookingConfirmationOptions} from '../tables/ContextMenu';
import {FilterOption} from '../tables/DataTable';

interface CarsTableProps {
    tableId?: string; // Unique ID for persisting preferences (defaults to 'cars-table')
    cars: Car[];
    loading: boolean;
    onLoadMore?: () => void;
    hasMore?: boolean;
    showVoyageColumn?: boolean;
    selectable?: boolean;  // Enable selection features (checkboxes, drag-select)
    onRowDoubleClick?: (car: Car) => void;
    onRowClick?: (car: Car) => void;
    onSelectionChange?: (selectedIds: string[]) => void;
    totalCars?: number; // Total cars in voyage (including unloaded pages)
    selectAllAcrossPages?: boolean; // Whether all cars across all pages are selected
    onSelectAllAcrossPages?: (selectAll: boolean) => void; // Callback to toggle select-all-across-pages
    selectedRowId?: string;
    scrollToRowId?: string;  // Scroll to this row when data loads
    onToggleLoaded?: (car: Car, newValue: boolean) => void | Promise<void>;
    loadingLoadedIds?: string[];
    searchQuery?: string;
    searchColumn?: string;
    onColumnClick?: (columnKey: string) => void;
    // Controlled sort props - if provided, use these instead of internal state (server-side sorting)
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc';
    onSort?: (column: string, direction: 'asc' | 'desc') => void;
    // Focus mode - right-click to focus on a range of cars around a reference number, chassis, shipper, or consignee
    onFocus?: (car: Car) => void;
    onFocusChassis?: (car: Car) => void;
    onFocusShipper?: (car: Car) => void;
    onFocusConsignee?: (car: Car) => void;
    // Highlighted values (for focus mode)
    highlightedRefNo?: number | null;
    highlightedChassis?: string | null;
    highlightedShipper?: string | null;
    highlightedConsignee?: string | null;
    // Server-side filtering props
    serverSideFilters?: boolean;
    onFiltersChange?: (filters: Record<string, string[]>) => void;
    getFilterOptions?: (columnKey: string, currentFilters?: Record<string, string[]>) => Promise<FilterOption[]>;
    columnFilters?: Record<string, string[]>;
    // Context menu props - for full right-click context menu with actions
    contextMenuGroups?: ContextMenuGroup[];
    buildContextMenuGroups?: (car: Car) => ContextMenuGroup[];
    onContextAction?: (action: ContextActionKey, car: Car) => void;
    onPrintBL?: (car: Car, options: PrintBLOptions) => void;
    onPrintBookingConfirmation?: (car: Car, options: PrintBookingConfirmationOptions) => void;
}

export const CarsTable: React.FC<CarsTableProps> = ({
                                                        tableId = 'cars-table',
                                                        cars,
                                                        loading,
                                                        onLoadMore,
                                                        hasMore = false,
                                                        showVoyageColumn = false,
                                                        selectable = true,  // Default to true for backwards compatibility
                                                        onRowDoubleClick,
                                                        onRowClick,
                                                        onSelectionChange,
                                                        totalCars,
                                                        selectAllAcrossPages = false,
                                                        onSelectAllAcrossPages,
                                                        selectedRowId,
                                                        scrollToRowId,
                                                        onToggleLoaded,
                                                        loadingLoadedIds,
                                                        searchQuery: externalSearchQuery,
                                                        searchColumn: externalSearchColumn,
                                                        onColumnClick,
                                                        sortColumn: controlledSortColumn,
                                                        sortDirection: controlledSortDirection,
                                                        onSort,
                                                        onFocus,
                                                        onFocusChassis,
                                                        onFocusShipper,
                                                        onFocusConsignee,
                                                        highlightedRefNo,
                                                        highlightedChassis,
                                                        highlightedShipper,
                                                        highlightedConsignee,
                                                        serverSideFilters = false,
                                                        onFiltersChange,
                                                        getFilterOptions,
                                                        columnFilters: controlledColumnFilters,
                                                        contextMenuGroups,
                                                        buildContextMenuGroups: buildContextMenuGroupsProp,
                                                        onContextAction,
                                                        onPrintBL,
                                                        onPrintBookingConfirmation,
                                                    }) => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [selectAll, setSelectAll] = useState(false);

    // Context menu state - supports both legacy focus-only menu and new full context menu
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
    const highlightedRowRef = useRef<HTMLTableRowElement>(null);

    // Column filters UI state (filter values come from useTablePreferences hook)
    const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
    const [filterSearch, setFilterSearch] = useState('');
    const [filterDropdownPosition, setFilterDropdownPosition] = useState<{ top: number; left: number } | null>(null);
    const filterDropdownRef = useRef<HTMLDivElement>(null);

    // Scroll to row tracking
    const hasScrolledToRowRef = useRef(false);
    const rowRefsMap = useRef<Map<string, HTMLTableRowElement>>(new Map());
    const [highlightedScrollToRow, setHighlightedScrollToRow] = useState<string | null>(null);

    // Close filter dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) {
                setOpenFilterColumn(null);
                setFilterSearch('');
                setFilterDropdownPosition(null);
            }
        };
        if (openFilterColumn) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [openFilterColumn]);

    // Scroll to highlighted row when focus mode is activated
    useEffect(() => {
        if ((highlightedRefNo !== null && highlightedRefNo !== undefined) || highlightedChassis) {
            if (highlightedRowRef.current) {
                setTimeout(() => {
                    highlightedRowRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
                }, 100);
            }
        }
    }, [highlightedRefNo, highlightedChassis]);

    // Reset scroll tracking when scrollToRowId changes (for new navigation)
    useEffect(() => {
        if (scrollToRowId) {
            hasScrolledToRowRef.current = false;
        }
    }, [scrollToRowId]);

    // Scroll to row when data loads (for navigation state restoration)
    useEffect(() => {
        if (scrollToRowId && !hasScrolledToRowRef.current && !loading && cars.length > 0) {
            const rowExists = cars.some(car => car.id === scrollToRowId);
            if (rowExists) {
                requestAnimationFrame(() => {
                    const rowElement = rowRefsMap.current.get(scrollToRowId);
                    if (rowElement) {
                        rowElement.scrollIntoView({behavior: 'auto', block: 'center'});
                        hasScrolledToRowRef.current = true;
                        // Highlight the row temporarily
                        setHighlightedScrollToRow(scrollToRowId);
                        // Clear highlight after animation
                        setTimeout(() => {
                            setHighlightedScrollToRow(null);
                        }, 2000);
                    }
                });
            }
        }
    }, [scrollToRowId, cars, loading]);

    // Get column definitions from shared module
    const columns: TableColumn<Car>[] = useMemo(
        () => getCarsColumns({includeVoyageColumn: showVoyageColumn}),
        [showVoyageColumn]
    );

    // Use the table preferences hook for persistent filter and sort state
    const {
        columnFilters: internalColumnFilters,
        setColumnFilter: internalSetColumnFilter,
        sortColumn: persistedSortColumn,
        sortDirection: persistedSortDirection,
        handleSort: persistedHandleSort,
    } = useTablePreferences({
        tableId,
        columns,
    });

    // Use controlled column filters if provided, otherwise use internal state
    const columnFilters = controlledColumnFilters ?? internalColumnFilters;
    const setColumnFilter = (key: string, values: string[]) => {
        // Always update internal state for local UI
        internalSetColumnFilter(key, values);
        // Notify parent of the change if callback provided (for controlled or server-side filtering)
        if (onFiltersChange) {
            const newFilters = {...columnFilters, [key]: values};
            onFiltersChange(newFilters);
        }
    };

    // State for server-side filter options
    const [serverFilterOptions, setServerFilterOptions] = useState<FilterOption[]>([]);
    const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);

    // Fetch filter options from server when filter dropdown opens
    useEffect(() => {
        if (openFilterColumn && serverSideFilters && getFilterOptions) {
            setLoadingFilterOptions(true);
            setServerFilterOptions([]);
            // Pass current filters to get dynamic options based on other active filters
            getFilterOptions(openFilterColumn, columnFilters)
                .then((options) => {
                    setServerFilterOptions(options);
                })
                .catch((err) => {
                    console.error('Failed to fetch filter options:', err);
                    setServerFilterOptions([]);
                })
                .finally(() => {
                    setLoadingFilterOptions(false);
                });
        }
    }, [openFilterColumn, serverSideFilters, getFilterOptions, columnFilters]);

    // Use external search if provided, otherwise use internal hook
    const internalSearch = useTableSearch({columns});
    const filterValue = externalSearchQuery ?? internalSearch.searchQuery;
    const filterColumn = externalSearchColumn ?? internalSearch.searchColumn;
    const handleColumnClick = onColumnClick ?? internalSearch.handleColumnClick;

    // Use server-side sorting when onSort is provided, otherwise fallback to persisted client-side
    const isServerSideSort = onSort !== undefined;

    // Use server-side sort state if provided, otherwise use persisted state from preferences
    const sortColumn = isServerSideSort ? (controlledSortColumn ?? null) : persistedSortColumn;
    const sortDirection = isServerSideSort ? (controlledSortDirection || 'asc') : persistedSortDirection;

    // When using server-side, data is already sorted; when client-side, sort using persisted state
    const sortedData = useMemo(() => {
        if (isServerSideSort) return cars;
        if (!persistedSortColumn) return cars;

        // Sort data client-side using the persisted sort state
        const column = columns.find(c => c.key === persistedSortColumn);
        if (!column) return cars;

        return [...cars].sort((a, b) => {
            const aValue = column.accessor ? column.accessor(a) : (a as any)[persistedSortColumn];
            const bValue = column.accessor ? column.accessor(b) : (b as any)[persistedSortColumn];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return persistedSortDirection === 'asc' ? 1 : -1;
            if (bValue == null) return persistedSortDirection === 'asc' ? -1 : 1;

            let comparison = 0;
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                comparison = aValue - bValue;
            } else {
                comparison = String(aValue).localeCompare(String(bValue));
            }

            return persistedSortDirection === 'asc' ? comparison : -comparison;
        });
    }, [cars, isServerSideSort, persistedSortColumn, persistedSortDirection, columns]);

    // Handle sort - use server-side if onSort is provided, otherwise persist to localStorage
    const handleSort = (columnKey: string) => {
        if (isServerSideSort && onSort) {
            // Server-side sorting: calculate new direction and call onSort
            const newDirection = sortColumn === columnKey && sortDirection === 'asc' ? 'desc' : 'asc';
            onSort(columnKey, newDirection);
        } else {
            // Client-side sorting: use persisted handler (saves to localStorage)
            persistedHandleSort(columnKey);
        }
    };

    // Notify parent of selection changes
    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(Array.from(selectedIds));
        }
    }, [selectedIds, onSelectionChange]);

    // Apply filtering to sorted data (search filter + column filters)
    const processedCars = useMemo(() => {
        let result = sortedData;

        // Apply search filter (client-side text search)
        if (filterColumn && filterValue) {
            result = filterRowsByColumn(result, columns, filterColumn, filterValue);
        }

        // Skip client-side column filtering when server-side filters are enabled
        // In server mode, data is already filtered by the backend
        if (!serverSideFilters) {
            // Apply column filters (checkbox-based) only for client-side filtering
            const activeFilters = Object.entries(columnFilters).filter(([_, values]) => values.length > 0);
            if (activeFilters.length > 0) {
                result = result.filter((car: Car) => {
                    return activeFilters.every(([columnKey, selectedValues]) => {
                        const column = columns.find(c => c.key === columnKey);
                        const cellValue = column?.accessor ? column.accessor(car) : (car as any)[columnKey];
                        const strValue = cellValue == null ? '' : String(cellValue);
                        return selectedValues.includes(strValue);
                    });
                });
            }
        }

        return result;
    }, [sortedData, filterColumn, filterValue, columns, columnFilters, serverSideFilters]);

    // Drag and select functionality
    const {
        isDraggingRef,
        hasMovedRef,
        handleCheckboxMouseDown,
        handleRowMouseEnter,
        handleRowMouseMove
    } = useDragSelect({
        selectedRows: selectedIds,
        onSelectionChange: (selectedIdsArray) => {
            const newSelected = new Set(selectedIdsArray);
            setSelectedIds(newSelected);
            // Always compare against processedCars - the filtered/visible rows
            setSelectAll(processedCars.length > 0 && newSelected.size === processedCars.length);
            onSelectionChange?.(selectedIdsArray);
        }
    });

    // Track expanded state for cars with related units
    const [expandedCars, setExpandedCars] = useState<Set<string>>(new Set());

    // Toggle expansion for a car's related units
    const toggleExpanded = (carId: string) => {
        setExpandedCars(prev => {
            const newSet = new Set(prev);
            if (newSet.has(carId)) {
                newSet.delete(carId);
            } else {
                newSet.add(carId);
            }
            return newSet;
        });
    };

    // Group cars with their related units from the related_cars array
    const groupedCars = useMemo(() => {
        const result: Array<{
            car: Car;
            relatedUnit?: RelatedCar;
            isRelated: boolean;
            isLastRelated?: boolean;
            parentHasMore?: boolean
        }> = [];

        processedCars.forEach(car => {
            // Add main car
            const hasRelated = car.related_cars && car.related_cars.length > 0;
            result.push({car, isRelated: false, parentHasMore: hasRelated});

            // Add related cars as sub-rows if expanded (sorted: Attached first, then STC)
            if (hasRelated && expandedCars.has(car.id)) {
                const sortedRelatedCars = [...car.related_cars!].sort((a, b) => {
                    if (a.relationship_type === 'Attached' && b.relationship_type !== 'Attached') return -1;
                    if (a.relationship_type !== 'Attached' && b.relationship_type === 'Attached') return 1;
                    return 0;
                });
                sortedRelatedCars.forEach((relatedCar, index) => {
                    const isLast = index === sortedRelatedCars.length - 1;
                    result.push({
                        car,
                        relatedUnit: relatedCar,
                        isRelated: true,
                        isLastRelated: isLast
                    });
                });
            }
        });

        return result;
    }, [processedCars, expandedCars]);

    // Handle select all - always use processedCars which has all filters applied
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedIds(new Set());
            setSelectAll(false);
            onSelectAllAcrossPages?.(false);
        } else {
            // Always use processedCars - it contains the filtered/visible rows
            // This ensures select all respects both text search and column filters
            if (processedCars.length === 0) {
                setSelectedIds(new Set());
                setSelectAll(false);
                return;
            }
            setSelectedIds(new Set(processedCars.map(car => car.id)));
            setSelectAll(true);
        }
    };

    // Handle "select all across pages" from the banner
    const handleSelectAllAcrossPages = () => {
        onSelectAllAcrossPages?.(true);
    };

    // Handle clearing the across-pages selection
    const handleClearAcrossPages = () => {
        setSelectedIds(new Set());
        setSelectAll(false);
        onSelectAllAcrossPages?.(false);
    };

    // Handle individual selection
    const handleSelect = (carId: string) => {
        // Exit across-pages mode when individually deselecting
        if (selectAllAcrossPages) {
            onSelectAllAcrossPages?.(false);
        }
        const newSelected = new Set(selectedIds);
        if (newSelected.has(carId)) {
            newSelected.delete(carId);
        } else {
            newSelected.add(carId);
        }
        setSelectedIds(newSelected);
        // Always compare against processedCars - the filtered/visible rows
        setSelectAll(processedCars.length > 0 && newSelected.size === processedCars.length);
    };

    // Render cell content
    const renderCell = (car: Car, columnKey: string) => {
        const column = columns.find(c => c.key === columnKey);
        if (!column) return '-';

        const value = getCellValue(car, column);

        // Handle undefined/null values
        if (value == null || value === undefined) {
            if (['freight', 'collect'].includes(columnKey)) {
                return '0';
            }
            return '-';
        }

        // Loaded checkbox column - show relation type for related cars
        if (columnKey === 'loaded') {
            // Show attachment type for related cars instead of loaded checkbox
            if (car.attachment_status) {
                return (
                    <span
                        className="px-1 py-0.5 text-[9px] font-medium rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
            {car.attachment_status}
          </span>
                );
            }

            const isChecked = car.loaded || false;
            const isBusy = loadingLoadedIds?.includes(car.id) ?? false;
            return (
                <input
                    type="checkbox"
                    className="h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    checked={isChecked}
                    disabled={isBusy}
                    onChange={(event) => {
                        event.stopPropagation();
                        onToggleLoaded?.(car, event.target.checked);
                    }}
                    onClick={(event) => event.stopPropagation()}
                />
            );
        }

        // Age column - hide for loaded units, highlight if >= 30 days
        if (columnKey === 'age' && typeof value === 'number') {
            if (car.loaded) {
                return '-';
            }
            if (value >= 30) {
                return <span
                    className="px-1 py-0.5 rounded bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300">{value}</span>;
            }
            return value;
        }

        // Numeric columns
        if (['freight', 'collect'].includes(columnKey)) {
            const numValue = Number(value);
            return !isNaN(numValue) ? numValue.toFixed(0) : '0';
        }

        // Columns with title attribute
        if (['make_model', 'chs_no'].includes(columnKey) && value !== '-') {
            const text = String(value);
            return <span title={text}>{text}</span>;
        }

        return String(value);
    };

    const getCellClasses = (columnKey: string) => {
        const base = 'px-1.5 py-1.5 text-xs text-gray-900 dark:text-gray-100 overflow-hidden whitespace-nowrap';
        if (MONOSPACE_COLUMNS.has(columnKey)) {
            return `${base} font-mono text-ellipsis`;
        }
        return `${base} truncate`;
    };

    // Helper to get column width style
    const getColumnWidthStyle = (column: TableColumn<Car>): React.CSSProperties => {
        if (column.width === undefined) return {};
        if (typeof column.width === 'number') {
            return {width: `${column.width}px`, minWidth: `${column.width}px`, maxWidth: `${column.width}px`};
        }
        return {width: column.width};
    };

    // Whether we can show the "select all across pages" banner
    // Hide when client-side text search is active (filterValue), as the IDs endpoint doesn't apply it
    const hasClientTextSearch = !!filterValue;
    const showSelectAllBanner = selectable && selectAll && !selectAllAcrossPages && !hasClientTextSearch
        && totalCars !== undefined && totalCars > processedCars.length;
    const showAllSelectedBanner = selectable && selectAllAcrossPages && totalCars !== undefined;

    return (
        <div className="bg-white dark:bg-gray-800">
            {/* Select all across pages banner */}
            {showSelectAllBanner && (
                <div className="bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800 px-4 py-2 text-sm text-center text-blue-700 dark:text-blue-300">
                    All {processedCars.length} cars on this page are selected.{' '}
                    <button
                        onClick={handleSelectAllAcrossPages}
                        className="font-semibold underline hover:text-blue-900 dark:hover:text-blue-100"
                    >
                        Select all {totalCars} cars
                    </button>
                </div>
            )}
            {showAllSelectedBanner && (
                <div className="bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800 px-4 py-2 text-sm text-center text-blue-700 dark:text-blue-300">
                    All {totalCars} cars are selected.{' '}
                    <button
                        onClick={handleClearAcrossPages}
                        className="font-semibold underline hover:text-blue-900 dark:hover:text-blue-100"
                    >
                        Clear selection
                    </button>
                </div>
            )}
            {/* Table */}
            <table className="w-full table-fixed">
                <thead
                    className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 sticky top-0 z-10 border-b border-gray-200 dark:border-gray-600">
                <tr>
                    {/* Select All Checkbox (only when selectable) */}
                    {selectable && (
                        <th className="w-10 px-3 py-2 text-left border-r border-gray-200 dark:border-gray-600">
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded transition-colors duration-200"
                                />
                                <span className="sr-only">Select all</span>
                            </label>
                        </th>
                    )}

                    {/* Column Headers */}
                    {columns.map(column => (
                        <th
                            key={column.key}
                            style={getColumnWidthStyle(column)}
                            className="px-1.5 py-2 text-left text-[10px] font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border-r border-gray-200 dark:border-gray-600 last:border-r-0 overflow-hidden"
                            onClick={() => {
                                handleColumnClick(column.key);
                                if (column.sortable) {
                                    handleSort(column.key);
                                }
                            }}
                        >
                            <div className="flex items-center gap-1 group">
                                <span className="truncate select-none">{column.label}</span>
                                <div className="flex items-center gap-0.5">
                                    {/* Filter icon */}
                                    {column.filterable && (
                                        <button
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (openFilterColumn === column.key) {
                                                    setOpenFilterColumn(null);
                                                    setFilterSearch('');
                                                    setFilterDropdownPosition(null);
                                                } else {
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    setFilterDropdownPosition({top: rect.bottom + 4, left: rect.left});
                                                    setOpenFilterColumn(column.key);
                                                    setFilterSearch('');
                                                }
                                            }}
                                            className={`p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors ${
                                                columnFilters[column.key]?.length ? 'text-blue-600' : 'text-gray-400'
                                            }`}
                                            title="Filter"
                                        >
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd"
                                                      d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                                                      clipRule="evenodd"/>
                                            </svg>
                                        </button>
                                    )}
                                    {column.sortable && sortColumn === column.key && (
                                        <svg
                                            className={`w-3 h-3 flex-shrink-0 text-blue-600 ${sortDirection === 'desc' ? 'rotate-180' : ''}`}
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"/>
                                        </svg>
                                    )}
                                </div>
                            </div>
                        </th>
                    ))}
                </tr>
                </thead>

                <tbody className="bg-white dark:bg-gray-800">
                {processedCars.length === 0 ? (
                    <tr>
                        <td colSpan={columns.length + (selectable ? 1 : 0)}
                            className="px-6 py-12 text-center border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                            <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                                <svg className="w-12 h-12 mb-4 text-gray-300 dark:text-gray-600" fill="none"
                                     stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
                                </svg>
                                <p className="text-lg font-medium mb-1">{loading ? 'Loading...' : 'No cars found'}</p>
                                <p className="text-sm text-gray-400 dark:text-gray-500">No data to display at the
                                    moment</p>
                            </div>
                        </td>
                    </tr>
                ) : (
                    (() => {
                        const allCarIds = groupedCars.filter(g => !g.isRelated).map(({car}) => car.id);
                        return groupedCars.map(({car, relatedUnit, isRelated, isLastRelated, parentHasMore}, index) => {
                            // Related unit sub-row
                            if (isRelated && relatedUnit) {
                                return (
                                    <tr
                                        key={`related-${relatedUnit.id}`}
                                        className="bg-gradient-to-r from-purple-50 via-purple-50 to-white dark:from-purple-900/20 dark:via-purple-900/10 dark:to-gray-800 border-b border-purple-100 dark:border-purple-800/30"
                                    >
                                        {/* Empty checkbox cell */}
                                        {selectable && (
                                            <td className="px-3 py-1 border-r border-gray-200 dark:border-gray-600">
                                                <div className="w-4"/>
                                            </td>
                                        )}
                                        {/* Tree connector + Related unit info spanning all columns */}
                                        <td
                                            colSpan={columns.length}
                                            className="px-2 py-1.5 border-r border-gray-200 dark:border-gray-600 last:border-r-0"
                                        >
                                            <div className="flex items-center gap-2 pl-2">
                                                {/* Tree branch connector */}
                                                <div className="flex items-center text-purple-400 dark:text-purple-500">
                                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"
                                                         stroke="currentColor" strokeWidth="2">
                                                        {isLastRelated ? (
                                                            <path d="M12 3v9h9" strokeLinecap="round"
                                                                  strokeLinejoin="round"/>
                                                        ) : (
                                                            <>
                                                                <path d="M12 3v18" strokeLinecap="round"/>
                                                                <path d="M12 12h9" strokeLinecap="round"/>
                                                            </>
                                                        )}
                                                    </svg>
                                                </div>
                                                {/* Relation type badge */}
                                                <span
                                                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                                        relatedUnit.relationship_type === 'STC'
                                                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'
                                                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                                                    }`}>
                              {relatedUnit.relationship_type}
                            </span>
                                                {/* Chassis number */}
                                                <span
                                                    className="font-mono text-xs text-gray-700 dark:text-gray-300 font-medium">
                              {relatedUnit.chassis_number}
                            </span>
                                                {/* Make/Model */}
                                                {(() => {
                                                    const make = relatedUnit.make?.name ?? '';
                                                    const model = relatedUnit.model?.name ?? '';
                                                    const makeModel = make && model ? `${make}/${model}` : (make || model || relatedUnit.manual_make_model);
                                                    return makeModel ? (
                                                        <span className="text-xs text-gray-600 dark:text-gray-400">
                                                            {makeModel}
                                                        </span>
                                                    ) : null;
                                                })()}
                                                {/* Remarks if any */}
                                                {relatedUnit.remarks && (
                                                    <span
                                                        className="text-[10px] text-gray-500 dark:text-gray-400 italic truncate max-w-[200px]"
                                                        title={relatedUnit.remarks}>
                                — {relatedUnit.remarks}
                              </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }

                            // Main car row
                            const isActiveRow = selectedRowId === car.id;
                            const isChecked = selectedIds.has(car.id);
                            const isHighlighted = highlightedRefNo === car.reference_number ||
                                (highlightedChassis && car.chassis_number === highlightedChassis) ||
                                (highlightedShipper && car.shipper_name === highlightedShipper) ||
                                (highlightedConsignee && car.consignee_name === highlightedConsignee);
                            const isScrollHighlighted = highlightedScrollToRow === car.id;
                            const hasRelatedUnits = car.related_cars && car.related_cars.length > 0;
                            const isExpanded = expandedCars.has(car.id);
                            const mainCarIndex = allCarIds.indexOf(car.id);

                            return (
                                <tr
                                    key={car.id}
                                    ref={(el) => {
                                        if (isHighlighted) {
                                            (highlightedRowRef as React.MutableRefObject<HTMLTableRowElement | null>).current = el;
                                        }
                                        if (el) {
                                            rowRefsMap.current.set(car.id, el);
                                        } else {
                                            rowRefsMap.current.delete(car.id);
                                        }
                                    }}
                                    onClick={() => onRowClick && onRowClick(car)}
                                    onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(car)}
                                    onContextMenu={(e) => {
                                        // Show context menu if we have the context menu system configured
                                        if ((contextMenuGroups || buildContextMenuGroupsProp) && onContextAction) {
                                            e.preventDefault();
                                            setContextMenu({car, position: {x: e.clientX, y: e.clientY}});
                                        }
                                    }}
                                    onMouseEnter={() => handleRowMouseEnter(car.id, mainCarIndex, allCarIds)}
                                    onMouseMove={() => handleRowMouseMove(car.id, mainCarIndex, allCarIds)}
                                    className={`border-b border-gray-100 dark:border-gray-700 last:border-b-0 cursor-pointer transition-all duration-300 ${
                                        isScrollHighlighted
                                            ? 'bg-green-100 dark:bg-green-900/50 ring-2 ring-green-500 dark:ring-green-400 animate-pulse'
                                            : isHighlighted
                                                ? 'bg-amber-100 dark:bg-amber-900/50 ring-2 ring-amber-500 dark:ring-amber-400'
                                                : isActiveRow
                                                    ? 'bg-blue-100 dark:bg-blue-800 ring-2 ring-blue-500 dark:ring-blue-400 hover:bg-blue-200 dark:hover:bg-blue-700'
                                                    : isChecked
                                                        ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-800'
                                                        : mainCarIndex % 2 === 0
                                                            ? 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                            : 'bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                                >
                                    {/* Checkbox column (only when selectable) */}
                                    {selectable && (
                                        <td className="px-3 py-2 border-r border-gray-200 dark:border-gray-600">
                                            <div className="flex items-center justify-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(car.id)}
                                                    onChange={(e) => {
                                                        if (!isDraggingRef.current && !hasMovedRef.current) {
                                                            handleSelect(car.id);
                                                        }
                                                    }}
                                                    onMouseDown={(e) => handleCheckboxMouseDown(e, car.id, mainCarIndex, allCarIds)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                            </div>
                                        </td>
                                    )}

                                    {columns.map((column, columnIndex) => (
                                        <td
                                            key={column.key}
                                            style={getColumnWidthStyle(column)}
                                            className={`${getCellClasses(column.key)} border-r border-gray-200 dark:border-gray-600 last:border-r-0`}
                                            title={typeof renderCell(car, column.key) === 'string' ? renderCell(car, column.key) as string : undefined}
                                        >
                                            {columnIndex === 0 && hasRelatedUnits ? (
                                                <div className="flex items-center gap-1">
                                                    <span>{renderCell(car, column.key)}</span>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleExpanded(car.id);
                                                        }}
                                                        className="p-1 rounded hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors group"
                                                        title={`${car.related_cars!.length} related unit${car.related_cars!.length > 1 ? 's' : ''}`}
                                                    >
                                                        <div className="flex items-center gap-0.5">
                                                            <svg
                                                                className={`w-3.5 h-3.5 text-purple-500 dark:text-purple-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                                                                fill="currentColor"
                                                                viewBox="0 0 20 20"
                                                            >
                                                                <path fillRule="evenodd"
                                                                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                      clipRule="evenodd"/>
                                                            </svg>
                                                            <span
                                                                className="text-[9px] font-bold text-purple-600 dark:text-purple-400 min-w-[12px]">
                                    {car.related_cars!.length}
                                  </span>
                                                        </div>
                                                    </button>
                                                </div>
                                            ) : (
                                                renderCell(car, column.key)
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            );
                        });
                    })()
                )}
                </tbody>
            </table>

            {/* New Context Menu - uses the ContextMenu component when context menu configuration is provided */}
            {contextMenu && (contextMenuGroups || buildContextMenuGroupsProp) && onContextAction && (
                <ContextMenu
                    car={contextMenu.car}
                    position={contextMenu.position}
                    groups={buildContextMenuGroupsProp ? buildContextMenuGroupsProp(contextMenu.car) : contextMenuGroups!}
                    onAction={onContextAction}
                    onClose={() => setContextMenu(null)}
                    onPrintBL={onPrintBL}
                    onPrintBookingConfirmation={onPrintBookingConfirmation}
                />
            )}

            {/* Filter dropdown portal */}
            {openFilterColumn && filterDropdownPosition && (() => {
                const column = columns.find(c => c.key === openFilterColumn);
                if (!column) return null;

                // For server-side filters, use serverFilterOptions; otherwise extract from data
                let uniqueValues: [string, number][];
                if (serverSideFilters && getFilterOptions) {
                    // Use server-fetched options
                    uniqueValues = serverFilterOptions.map(opt => [opt.value, opt.count] as [string, number]);
                } else {
                    // Get unique values and their counts from all cars (not just filtered)
                    const valueCounts = new Map<string, number>();
                    cars.forEach((car: Car) => {
                        const cellValue = column.accessor ? column.accessor(car) : (car as any)[column.key];
                        const strValue = cellValue == null ? '' : String(cellValue);
                        valueCounts.set(strValue, (valueCounts.get(strValue) || 0) + 1);
                    });
                    uniqueValues = Array.from(valueCounts.entries())
                        .sort((a, b) => a[0].localeCompare(b[0]));
                }

                // Filter by search
                const filteredValues = filterSearch
                    ? uniqueValues.filter(([val]) => val.toLowerCase().includes(filterSearch.toLowerCase()))
                    : uniqueValues;

                const selectedValues = columnFilters[column.key] || [];

                return createPortal(
                    <div
                        ref={filterDropdownRef}
                        className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg min-w-[280px]"
                        style={{top: filterDropdownPosition.top, left: filterDropdownPosition.left}}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {serverSideFilters ? 'Filter' : 'Local Filter'} by '{column.label}'
                            </div>
                        </div>

                        {/* Search input */}
                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                            <div className="relative">
                                <svg
                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                </svg>
                                <input
                                    type="text"
                                    value={filterSearch}
                                    onChange={(e) => setFilterSearch(e.target.value)}
                                    placeholder=""
                                    className="w-full pl-9 pr-3 py-1.5 text-sm border border-blue-400 dark:border-blue-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* Loading state for server-side filters */}
                        {loadingFilterOptions && (
                            <div className="flex items-center justify-center py-4">
                                <div
                                    className="animate-spin rounded-full h-5 w-5 border-2 border-blue-200 border-t-blue-600"></div>
                                <span className="ml-2 text-sm text-gray-500">Loading options...</span>
                            </div>
                        )}

                        {/* Header row */}
                        {!loadingFilterOptions && (
                            <div
                                className="flex items-center px-3 py-1.5 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                                <input
                                    type="checkbox"
                                    checked={selectedValues.length === uniqueValues.length && uniqueValues.length > 0}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setColumnFilter(column.key, uniqueValues.map(([v]) => v));
                                        } else {
                                            setColumnFilter(column.key, []);
                                        }
                                    }}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded mr-3"
                                />
                                <span
                                    className="flex-1 text-xs font-medium text-gray-600 dark:text-gray-400">Value</span>
                                <span
                                    className="text-xs font-medium text-gray-600 dark:text-gray-400 w-12 text-right">Count</span>
                            </div>
                        )}

                        {/* Values list */}
                        {!loadingFilterOptions && (
                            <div className="max-h-64 overflow-y-auto">
                                {filteredValues.map(([value, count]) => (
                                    <label
                                        key={value || '__empty__'}
                                        className="flex items-center px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedValues.includes(value)}
                                            onChange={(e) => {
                                                const currentFilters = columnFilters[column.key] || [];
                                                if (e.target.checked) {
                                                    setColumnFilter(column.key, [...currentFilters, value]);
                                                } else {
                                                    setColumnFilter(column.key, currentFilters.filter(v => v !== value));
                                                }
                                            }}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded mr-3"
                                        />
                                        <span
                                            className="flex-1 text-sm text-gray-700 dark:text-gray-300 truncate">{value || '(empty)'}</span>
                                        <span
                                            className="text-sm text-gray-500 dark:text-gray-400 w-12 text-right">{count}</span>
                                    </label>
                                ))}
                            </div>
                        )}

                        {/* Footer */}
                        <div
                            className="px-3 py-2 border-t border-gray-200 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400">
                            {'Select a checkbox to filter rows'}
                        </div>
                    </div>,
                    document.body
                );
            })()}
        </div>
    );
};
